import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Database, MessageSquare } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { MessageList } from '@/components/chat/message-list';
import { ChatInput } from '@/components/chat/input';
import { ChatEmptyState } from '@/components/chat/empty-state';
import { SuggestedPrompts } from '@/components/chat/suggested-prompts';
import { UserMessage } from '@/components/chat/user-message';
import { AiMessage } from '@/components/chat/ai-message';
import {
  usePostChatMessageMutation,
  useChatTranscriptQuery,
  type ChatMessageResult,
  type ChatTranscriptMessage,
} from '@/lib/api/chat';
import { useEngagementQuery } from '@/lib/api/engagements';
import { useDocumentProcessingQuery } from '@/lib/api/document-processing';
import { countIndexedDocuments } from '@/components/documents/indexed-documents';
import { cn } from '@/lib/utils/cn';

/**
 * C20-138 — Engagement chat screen. Prototype-true rebuild aligned with
 * `design_handoff_caliber_v2/prototype/screens-engagement.js` →
 * `Screens.chat` (lines 545-607).
 *
 * Prototype contract:
 *   1. Header row: title "Engagement chat" + subtitle
 *      "Scoped to {client} · {project} · {N} indexed documents".
 *      Right side: `chip-unassessed` with database icon "Scoped to {client}"
 *      (the prototype's "History" button is left out until there are
 *      past sessions to list).
 *   2. Single card with `grid-template-rows:1fr auto`:
 *      - Top: scrollable transcript (AI bubbles + user bubbles).
 *      - Bottom: paper-50 footer panel containing the input box
 *        (textarea + Send; the prototype's paperclip is left out until
 *        attaching a scope works) and a chip row of suggested prompts.
 *   3. AI bubbles use `<AiSurface>` with the lavender left rail,
 *      `<AiConfidenceChip>` pinned right, and a `<CitationChip>` row
 *      after the answer.
 *   4. User bubbles are right-aligned paper-100 with no chip decoration.
 *
 * Element-by-element wire-up matrix (PR body has the same):
 *
 *   - Title "Engagement chat" → static copy (prototype literal)
 *   - Subtitle client/project name → `useEngagementQuery` (real DTO)
 *   - Subtitle indexed-documents count → `useDocumentProcessingQuery`
 *     counted by `countIndexedDocuments`: the live documents whose row on
 *     the Documents screen reads Indexed (same list, same definition, so
 *     the two screens agree). It used to count classification rows, which
 *     include deleted documents and say nothing about indexing.
 *   - Header "Scoped to {client}" chip → `useEngagementQuery.client_name`
 *   - Transcript messages → `useChatTranscriptQuery` (T-0053 backend)
 *   - Send message → `usePostChatMessageMutation` (T-0053 backend)
 *   - Citations on each AI bubble → real `citations[]` from the API
 *     (zod-validated via `ChatCitationSchema`)
 *   - Confidence chip on each AI bubble → `deriveMessageConfidence`
 *     (API field when present, fallback from citation set)
 *   - "answered in {latency}" stamp → measured client-side (request
 *     submit → response landed); real elapsed, not synthesized
 *   - Citation chip click → opens `<CitationSourceModal>` (T-0093 /
 *     T-0055 / T-0125 round-trip)
 *   - Suggested prompts → static list from the prototype (3 prompts);
 *     selecting one submits via the same `postChatMessage` path
 *   - No-evidence answer → `<AiMessage noEvidence>` refusal card (no
 *     fabrication path; the API surfaces `no_evidence=true` and the
 *     bubble shows the refusal copy)
 *
 * Not built yet, so not shown (C2-32: no disabled "pending" controls):
 *   - Listing past chat sessions (no list-sessions endpoint). The "Start a
 *     new session" footer link works — it resets the `sessionId` so the
 *     next message mints a fresh session (the prior transcript stays in
 *     the DB append-only per spec §4).
 *   - Attaching a scope to a question.
 *
 * Hard rules followed:
 *   - Tokens-only colors (paper-0/50/100, ink-100/200/500/700/900,
 *     ai-surface, posture-unassessed); load-bearing primitives reused
 *     from `apps/web/src/components/ui/`.
 *   - Visual contract = prototype.
 *   - No mock data, and no controls for features that don't exist yet.
 */
export const Route = createFileRoute('/engagements/$engagementId/chat')({
  component: ChatTab,
});

// Prototype's three suggested-prompt chips (lines 600-602 of
// screens-engagement.js). Order matters — read left-to-right.
const SUGGESTED_PROMPTS = [
  'What changed in the last status report?',
  'Summarize defect trend',
  'Which deliverables are at risk?',
] as const;

// Streaming reveal: ~1s total wall time, by word. We pick word-by-word
// (not char-by-char) because char streaming reads as jittery on dense
// paragraphs; word reveal feels closer to model token streaming.
const STREAM_TOTAL_MS = 1000;
const STREAM_MIN_TICK_MS = 16; // one animation frame

function ChatTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/chat' });
  const [question, setQuestion] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Optimistic in-flight user turn — held only between submit and the
  // API's response. After the response lands, the assistant turn flows
  // through the streaming reveal; once revealed, it's part of the
  // transcript (and we drop the optimistic copy).
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);

  // The most-recent assistant turn the parent is "streaming" via the
  // faked reveal. While this is non-null, the bubble shows
  // `displayContent` (= a prefix of `result.answer`) and the input is
  // still disabled.
  const [streamingResult, setStreamingResult] = useState<ChatMessageResult | null>(null);
  const [streamedSoFar, setStreamedSoFar] = useState('');
  const streamTimer = useRef<number | null>(null);

  // Client-side measured latency — set when the API response lands. The
  // backend doesn't return a latency stamp so we measure it locally
  // (submit → response). Read by `<AiMessage>` as the "answered in Xs"
  // header stamp on the most-recent assistant bubble; once the
  // streaming reveal completes and the canonical transcript re-paints,
  // older messages don't show the stamp (the API doesn't persist it).
  const [lastLatencyMs, setLastLatencyMs] = useState<number | null>(null);
  const submitStartRef = useRef<number | null>(null);

  // Force-scroll-to-bottom counter — bumped on user submit so the
  // textarea moves into view regardless of the user's prior pin.
  const [scrollKey, setScrollKey] = useState(0);

  const transcript = useChatTranscriptQuery(engagementId, sessionId);
  const postMessage = usePostChatMessageMutation(engagementId);

  // Real-data subtitle reads. `useEngagementQuery` gives us the client +
  // project names; the documents list gives the count of indexed documents,
  // counted as the Documents screen counts them (same list and query, so
  // the number always agrees with "M indexed" there).
  const engagementQ = useEngagementQuery(engagementId);
  const documentsQ = useDocumentProcessingQuery(engagementId);
  const clientName = engagementQ.data?.client_name ?? null;
  const projectName = engagementQ.data?.project_name ?? null;
  const indexedCount = documentsQ.data ? countIndexedDocuments(documentsQ.data.documents) : null;

  // Cancel the in-flight reveal cleanly. The API turn itself can't be
  // cancelled mid-flight today (the backend is synchronous and not
  // AbortSignal-aware); v1 cancel only halts the *client-side* reveal
  // so the user can read the (already-paid-for) answer immediately.
  const cancelStream = useCallback(() => {
    if (streamTimer.current !== null) {
      window.clearTimeout(streamTimer.current);
      streamTimer.current = null;
    }
    setStreamingResult(null);
    setStreamedSoFar('');
    setPendingQuestion(null);
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (streamTimer.current !== null) {
        window.clearTimeout(streamTimer.current);
        streamTimer.current = null;
      }
    };
  }, []);

  // Drive the faked streaming reveal. When `streamingResult` flips to
  // non-null, we walk the answer word-by-word, updating
  // `streamedSoFar` until it equals the full answer; then we clear
  // `streamingResult` (which lets the next turn proceed) and re-fetch
  // the transcript so the canonical row replaces the local one.
  useEffect(() => {
    if (!streamingResult) return;
    const fullAnswer = streamingResult.answer;
    // No-evidence answers are short refusal copy — skip the reveal so
    // the user sees the refusal immediately. The bubble has its own
    // refusal card; we render with `streaming: false`.
    if (streamingResult.no_evidence) {
      setStreamedSoFar(fullAnswer);
      streamTimer.current = window.setTimeout(() => {
        finishStream();
      }, 250);
      return;
    }
    const words = fullAnswer.split(/(\s+)/); // keep whitespace tokens
    const totalChunks = words.length;
    if (totalChunks <= 1) {
      setStreamedSoFar(fullAnswer);
      streamTimer.current = window.setTimeout(() => finishStream(), 200);
      return;
    }
    const tickMs = Math.max(STREAM_MIN_TICK_MS, Math.floor(STREAM_TOTAL_MS / totalChunks));
    let i = 0;
    function step() {
      i += 1;
      const next = words.slice(0, i).join('');
      setStreamedSoFar(next);
      if (i >= totalChunks) {
        finishStream();
        return;
      }
      streamTimer.current = window.setTimeout(step, tickMs);
    }
    streamTimer.current = window.setTimeout(step, tickMs);
    return () => {
      if (streamTimer.current !== null) {
        window.clearTimeout(streamTimer.current);
        streamTimer.current = null;
      }
    };
    // We only re-run when the streamingResult identity changes — the
    // tick loop owns the rest of the reveal lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamingResult]);

  function finishStream() {
    if (streamTimer.current !== null) {
      window.clearTimeout(streamTimer.current);
      streamTimer.current = null;
    }
    // Drop the optimistic + streaming state — the transcript query
    // invalidation from `usePostChatMessageMutation.onSuccess` has
    // already re-fetched the canonical row by now (or will any moment),
    // so the persisted turn will paint and the local one disappears.
    setStreamingResult(null);
    setStreamedSoFar('');
    setPendingQuestion(null);
  }

  async function handleSubmit(text: string) {
    if (postMessage.isPending || streamingResult !== null) return;
    setQuestion('');
    setPendingQuestion(text);
    setScrollKey((k) => k + 1);
    submitStartRef.current = performance.now();
    try {
      const result = await postMessage.mutateAsync({
        question: text,
        session_id: sessionId,
      });
      // Real measured latency (submit → response). Used by the most-
      // recent bubble's "answered in Xs" stamp.
      if (submitStartRef.current !== null) {
        setLastLatencyMs(Math.max(0, Math.round(performance.now() - submitStartRef.current)));
      }
      // Adopt the (possibly newly-minted) session id.
      setSessionId(result.session_id);
      // Hand off to the streaming reveal.
      setStreamingResult(result);
      setStreamedSoFar('');
      setScrollKey((k) => k + 1);
    } catch {
      // Error already surfaced via postMessage.error below; drop the
      // optimistic user copy so the input is usable again.
      setPendingQuestion(null);
    }
  }

  function handleStartNewSession() {
    if (postMessage.isPending || streamingResult !== null) return;
    setSessionId(null);
    setQuestion('');
    setPendingQuestion(null);
    setStreamingResult(null);
    setStreamedSoFar('');
    setLastLatencyMs(null);
  }

  const persistedMessages: readonly ChatTranscriptMessage[] = transcript.data?.messages ?? [];

  // While streaming, we're showing the optimistic assistant bubble
  // INSTEAD of the persisted one (the persisted one may have arrived
  // via the invalidation but it would race with our reveal). Hide it
  // until the reveal finishes.
  const displayedMessages = useMemo(() => {
    if (!streamingResult) return persistedMessages;
    return persistedMessages.filter((m) => m.message_id !== streamingResult.message_id);
  }, [persistedMessages, streamingResult]);

  const isEmpty = displayedMessages.length === 0 && !pendingQuestion && !streamingResult;
  const pending = postMessage.isPending || streamingResult !== null;

  return (
    // One column that can't grow past the panel (`minmax(0,1fr)`): nothing
    // inside — a long title, a citation chip — can push the chat past the
    // right edge at tablet width (C2-35).
    <div className="grid h-[calc(100vh-13rem)] grid-cols-[minmax(0,1fr)] grid-rows-[auto_1fr] gap-4">
      <ChatHeader clientName={clientName} projectName={projectName} indexedCount={indexedCount} />

      {/* Single card: grid 1fr auto — transcript on top, input + prompts
          panel pinned to the bottom. Mirrors the prototype's
          `display:grid;grid-template-rows:1fr auto;height:calc(100vh - 220px)`
          rule. */}
      <div className="grid min-h-0 grid-rows-[1fr_auto] overflow-hidden rounded-card border border-ink-100 bg-paper-0">
        <div className="relative min-h-0 overflow-hidden">
          {isEmpty ? (
            <ChatEmptyState />
          ) : (
            <MessageList
              engagementId={engagementId}
              messages={displayedMessages}
              scrollToBottomKey={scrollKey}
              optimistic={
                pendingQuestion ? (
                  <>
                    <UserMessage content={pendingQuestion} />
                    {streamingResult ? (
                      <AiMessage
                        engagementId={engagementId}
                        content={streamingResult.answer}
                        displayContent={streamedSoFar}
                        confidence={streamingResult.confidence ?? null}
                        noEvidence={streamingResult.no_evidence}
                        tokenCostCents={streamingResult.token_cost_cents ?? null}
                        citations={streamingResult.citations}
                        latencyMs={lastLatencyMs}
                        streaming={streamedSoFar !== streamingResult.answer}
                      />
                    ) : (
                      <TypingIndicator />
                    )}
                  </>
                ) : null
              }
              footer={
                <>
                  {postMessage.error ? (
                    <div className="px-1">
                      <ErrorMessage error={postMessage.error} />
                    </div>
                  ) : null}
                  {transcript.error ? (
                    <div className="px-1">
                      <ErrorMessage error={transcript.error} />
                    </div>
                  ) : null}
                </>
              }
            />
          )}
        </div>

        {/* Input footer panel — paper-50 background, ink-100 top border.
            Contains the input box + the suggested-prompts chip row,
            always visible (not only on empty state). Matches the
            prototype's `border-top:1px solid var(--ink-100);padding:14px 24px;
            background:var(--paper-50)` rule. */}
        <div className="border-t border-ink-100 bg-paper-50 px-6 py-3.5">
          <ChatInput
            value={question}
            onChange={setQuestion}
            onSubmit={handleSubmit}
            pending={pending}
            onCancel={cancelStream}
          />
          <div className="mt-2">
            <SuggestedPrompts
              prompts={SUGGESTED_PROMPTS}
              onPick={(p) => {
                setQuestion(p);
                void handleSubmit(p);
              }}
              disabled={pending}
            />
          </div>
        </div>
      </div>

      {sessionId !== null ? (
        <div className="-mt-2 text-right">
          <button
            type="button"
            onClick={handleStartNewSession}
            disabled={pending}
            className={cn(
              'text-[12px] font-medium text-accent-amber-700 hover:underline',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Start a new session
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Header strip. Prototype contract (lines 547-556 of
 * `screens-engagement.js`):
 *
 *   - Left: title "Engagement chat" + subtitle
 *     "Scoped to {client} · {project} · {N} indexed documents".
 *   - Right: `chip-unassessed` with database icon "Scoped to {client}".
 *
 * Both header pieces come from real backend reads — never hardcoded. The
 * prototype's "History" button is left out until there is a list of past
 * sessions to open: a permanently disabled button with a "backend
 * follow-up" tooltip was developer copy (C2-32).
 *
 * The header wraps: at tablet width the chip drops under the title rather
 * than pushing the title past the right edge (C2-35).
 */
function ChatHeader({
  clientName,
  projectName,
  indexedCount,
}: {
  clientName: string | null;
  projectName: string | null;
  indexedCount: number | null;
}) {
  // Subtitle pieces — every chunk wired to real data, joined with
  // " · " separators. When a read hasn't landed yet we skip the piece
  // rather than render placeholder text (no mock data).
  const subtitleParts: string[] = [];
  if (clientName) subtitleParts.push(`Scoped to ${clientName}`);
  if (projectName) subtitleParts.push(projectName);
  if (typeof indexedCount === 'number') {
    subtitleParts.push(`${indexedCount} indexed document${indexedCount === 1 ? '' : 's'}`);
  }
  const subtitle = subtitleParts.join(' · ');

  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-h1 font-semibold text-ink-900">Engagement chat</h2>
        <p className="mt-1 text-meta text-ink-500 [overflow-wrap:anywhere]">
          {subtitle.length > 0 ? subtitle : <span className="opacity-0">·</span>}
        </p>
      </div>
      <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
        {/* "Scoped to {client}" chip — `chip-unassessed` variant in the
            prototype (ink-100 fill, posture-unassessed ink). Only renders
            once the engagement read lands; never shows a placeholder name. */}
        {clientName ? (
          <span
            title={`Scoped to ${clientName}`}
            className={cn(
              'inline-flex h-[22px] max-w-full items-center gap-1.5 whitespace-nowrap rounded-chip',
              'bg-ink-100 px-2.5 text-[12px] font-medium text-posture-unassessed',
            )}
          >
            <Database className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">Scoped to {clientName}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Three-dot typing indicator inside an AI-surface bubble. Shown while
 * the POST is in flight (before we have answer content to stream).
 * Animation is `prefers-reduced-motion`-safe per Tailwind's defaults.
 */
function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="w-6 shrink-0" aria-hidden="true" />
      <div
        role="status"
        aria-label="Generating answer"
        className={cn(
          'flex items-center gap-1.5 bg-ai-surface text-ai-ink',
          'rounded-[0_12px_12px_12px] border-l-2 border-ai-surface-border px-[18px] py-[14px]',
        )}
      >
        <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-ai-ink [animation-delay:-0.3s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-ai-ink [animation-delay:-0.15s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-ai-ink" />
        <span className="ml-2 text-[12px] font-medium">Caliber is thinking…</span>
      </div>
    </div>
  );
}
